import { FC, useMemo } from "react";
import { useHistory } from "react-router";

import { IUser } from "Auth/types";
import { Navbar as Component } from "shared/components";
import { BAND_PATHS } from "Bands/router";
import { BandToggle } from "Bands/containers";
import { copy } from "shared/assets";
import {
  useBandIdFromPath,
  useBandRoute,
  useCompanyIdFromPath,
  useCompanyRoute,
} from "Bands/utils";
import { useProfileContext } from "Auth/contexts";
import { usePermissionsContext } from "Bands/contexts";

interface NavbarProps {
  currentUser?: IUser;
}

export const Navbar: FC<NavbarProps> = ({ currentUser }) => {
  const history = useHistory();
  const bandId = useBandIdFromPath();
  const companyId = useCompanyIdFromPath();
  const { isAdmin } = useProfileContext();
  const { userCanManageBand } = usePermissionsContext();

  const { getPath: getBandPath } = useBandRoute();
  const { getPath: getCompanyPath } = useCompanyRoute();

  const navItems = useMemo(() => {
    const navItems = [];

    if (currentUser) {
      if (companyId) {
        navItems.push(
          {
            to: getCompanyPath(BAND_PATHS.COMPANY_DASHBOARD),
            label: copy.companyDashboard.navLink,
            dataTestId: "navbar_company_dashboard",
          },
          {
            to: getCompanyPath(BAND_PATHS.COMPANY_INFO),
            label: copy.companyInfo.navLink,
            dataTestId: "navbar_company_info",
          }
        );
      } else if (isAdmin) {
        navItems.push({
          to: BAND_PATHS.ADMIN_DASHBOARD,
          label: copy.adminDashboard.navLink,
          dataTestId: "navbar_admin_dashboard",
        });
      } else {
        navItems.push({
          to: bandId ? getBandPath(BAND_PATHS.BAND_CALENDAR) : BAND_PATHS.CALENDAR,
          label: copy.calendar.title,
          dataTestId: "navbar_calendar",
        });
      }

      if (bandId) {
        navItems.push({
          to: getBandPath(BAND_PATHS.MEMBERS),
          label: copy.members.navLabel,
          dataTestId: "navbar_members",
        });

        if (userCanManageBand) {
          navItems.push({
            to: getBandPath(BAND_PATHS.BAND_SETTINGS),
            label: copy.bandSettings.navLabel,
            dataTestId: "navbar_settings",
          });
        }
        navItems.push({
          to: getBandPath(BAND_PATHS.BAND_ASSETS),
          label: copy.bandAssets.navLabel,
          dataTestId: "navbar_assets",
        });
      }
    }

    return navItems;
  }, [bandId, companyId, currentUser, getBandPath, getCompanyPath, isAdmin, userCanManageBand]);

  const mainNavItem = useMemo(
    () => currentUser && <BandToggle companyId={companyId} bandId={bandId} />,
    [currentUser, bandId, companyId]
  );

  if (isAdmin)
    return (
      <Component
        title={copy.title.admin}
        navItems={navItems}
        currentUser={currentUser}
        profilePagePath={BAND_PATHS.PROFILE}
        homePagePath={BAND_PATHS.ADMIN_DASHBOARD}
        currentPath={history.location.pathname}
      />
    );

  return (
    <Component
      title={!currentUser ? copy.title.bands : ""}
      mainNavItem={mainNavItem}
      navItems={navItems}
      currentUser={currentUser}
      profilePagePath={BAND_PATHS.PROFILE}
      homePagePath={BAND_PATHS.CALENDAR}
      currentPath={history.location.pathname}
    />
  );
};
